/**
 * Rectangle and Circle — `docs/property-maps-v2.md` §5.3, §9.3.
 *
 * One class again, because both are a drag from a first point to a second:
 * the rectangle reads the second as the opposite corner, the circle as a
 * point on the rim. The difference is two lines, and the pointer handling —
 * press, drag, release, cancel on Escape — is the part worth having once.
 *
 * **Rectangles are axis-aligned**, because the map never rotates (§5.3). A
 * rotated rectangle is drawn with Area, which is the honest answer: there is
 * nowhere to put a rotation handle on a map that cannot rotate under it.
 *
 * **⇧ makes it a square**, and for a circle it does nothing — a circle is
 * already as constrained as it gets, and a modifier that silently does
 * nothing is better than one that invents a meaning.
 *
 * **The `shape` blob is what keeps it editable as itself.** A rectangle whose
 * geometry is a four-corner polygon and nothing else can only be re-edited
 * corner by corner; `{ kind: 'rect', a, b }` is what lets the select tool
 * offer two handles and keep the right angles.
 */
import { circlePolygon, metresBetween, metresToFeet, rectPolygon } from '@rowted/shared'
import type { LngLat } from '@rowted/shared'
import type { Draft, DrawEvent, DrawTool, KeyEvent, ScreenPoint, ToolOptions } from './types.js'

/** A drag shorter than this is a click somebody did not mean as a shape. */
const MIN_DRAG_PX = 4

export class DragTool implements DrawTool {
  readonly kind: 'rect' | 'circle'

  readonly hint: string

  readonly autoFinish = true


  private readonly frame: ToolOptions['frame']

  private readonly snap: NonNullable<ToolOptions['snap']>

  private from: ScreenPoint | null = null

  private to: ScreenPoint | null = null

  private dragging = false

  constructor(kind: 'rect' | 'circle', options: ToolOptions) {
    this.kind = kind
    this.frame = options.frame
    this.snap = options.snap ?? ((point) => point)
    this.hint = kind === 'rect'
      ? 'Drag a corner to the other · hold ⇧ for a square'
      : 'Drag from the centre out'
  }

  onPointerDown(event: DrawEvent): void {
    this.from = this.snap(event.point, event)
    this.to = this.from
    this.dragging = true
  }

  onPointerMove(event: DrawEvent): void {
    if (!this.dragging) return
    this.to = this.square(this.snap(event.point, event), event)
  }

  onPointerUp(event: DrawEvent): void {
    if (!this.dragging) return
    this.to = this.square(this.snap(event.point, event), event)
    this.dragging = false
  }

  onKey(event: KeyEvent): void {
    if (event.key === 'Escape') this.cancel()
  }

  cancel(): void {
    this.from = null
    this.to = null
    this.dragging = false
  }

  draft(): Draft | null {
    return this.build()
  }

  finish(): Draft | null {
    const made = this.build()
    if (!made?.complete) return null
    this.cancel()
    return made
  }

  private build(): Draft | null {
    if (!this.from || !this.to) return null
    const big = Math.hypot(this.to.x - this.from.x, this.to.y - this.from.y) >= MIN_DRAG_PX
    const a = this.frame.toLngLat(this.from)
    const b = this.frame.toLngLat(this.to)

    if (this.kind === 'rect') {
      return {
        kind: 'rect',
        geometry: rectPolygon(a, b),
        shape: { kind: 'rect', a, b },
        complete: big && !this.dragging,
      }
    }

    const radiusM = metresBetween(a, b)
    return {
      kind: 'circle',
      geometry: circlePolygon(a, Math.max(radiusM, 0.01)),
      shape: { kind: 'circle', center: a, radiusFt: metresToFeet(radiusM) },
      complete: big && !this.dragging,
    }
  }

  /** `⇧` on a rectangle: the shorter side becomes the longer one. */
  private square(point: ScreenPoint, event: DrawEvent): ScreenPoint {
    if (!event.shift || this.kind !== 'rect' || !this.from) return point
    const dx = point.x - this.from.x
    const dy = point.y - this.from.y
    const side = Math.max(Math.abs(dx), Math.abs(dy))
    return {
      x: this.from.x + Math.sign(dx || 1) * side,
      y: this.from.y + Math.sign(dy || 1) * side,
    }
  }
}

/** Where a drag ended, in the world — exported for the select tool's handles. */
export const cornersOf = (shape: unknown): { a: LngLat; b: LngLat } | null => {
  const blob = shape as { kind?: string; a?: LngLat; b?: LngLat } | null
  return blob?.kind === 'rect' && blob.a && blob.b ? { a: blob.a, b: blob.b } : null
}
